import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useReviews } from "../../hooks/useReviews";
import { useNotification } from "../context/NotificationContext";
import ReviewSessionList from "./reviewSessionList";
import StudySession from "./studySession";
import ProgressSection from "./progressSection";
import CalendarWidget from "./calendarWidget";
import { BookOpen, Clock, TrendingUp, Play } from "lucide-react";

const SpacedRepetitionDashboard: React.FC = () => {
  const { t } = useTranslation();
  const {
    pendingReviews,
    totalPendingCount,
    upcoming7DaysCount,
    totalUpcomingCount,
    upcomingPagination,
    pendingPagination,
    loading,
    error,
    completeReview,
    getGroupedSessions,
    fetchAllReviews,
    fetchUpcomingReviews,
    fetchPendingReviews,
    fetchAllPendingReviewsForSession,
  } = useReviews();

  const { showSuccess, showError } = useNotification();
  const [currentSession, setCurrentSession] = useState<number>(0);
  const [showStudySession, setShowStudySession] = useState(false);

  const handleSessionsUpdate = useCallback(() => {
    fetchAllReviews();
  }, [fetchAllReviews]);

  const handleUpcomingPageChange = useCallback(
    (page: number) => {
      fetchUpcomingReviews(7, page, 5);
    },
    [fetchUpcomingReviews]
  );

  const handlePendingPageChange = useCallback(
    (page: number) => {
      fetchPendingReviews(page, 5);
    },
    [fetchPendingReviews]
  );

  const startStudySession = async () => {
    if (totalPendingCount > 0) {
      // Obtener todas las tarjetas pendientes sin paginación para la sesión
      try {
        await fetchAllPendingReviewsForSession();
        setCurrentSession(0);
        setShowStudySession(true);
      } catch (error) {
        console.error(
          "Error cargando todas las tarjetas para la sesión:",
          error
        );
        showError(
          "Error",
          "No se pudieron cargar todas las tarjetas para la sesión"
        );
      }
    }
  };

  const handleCompleteReview = async (difficulty: 1 | 2 | 3) => {
    if (currentSession >= pendingReviews.length) return;

    const currentReview = pendingReviews[currentSession];

    try {
      await completeReview(currentReview.id, difficulty);
      // No avanzar automáticamente, el usuario debe usar los botones de navegación
    } catch (error) {
      console.error("Error completando revisión:", error);
      showError(
        "Error al completar revisión",
        "No se pudo guardar el progreso. Inténtalo de nuevo."
      );
    }
  };

  const handleNext = () => {
    if (currentSession < pendingReviews.length - 1) {
      setCurrentSession((prev) => prev + 1);
    } else {
      // Si llegamos al final, la sesión está completa
      showSuccess(
        "¡Sesión completada!",
        `Has terminado todas las ${totalPendingCount} tarjetas de hoy. ¡Excelente trabajo!`
      );
      setShowStudySession(false);
      setCurrentSession(0);
      // Recargar las revisiones para actualizar el conteo
      fetchAllReviews();
    }
  };

  const handlePrevious = () => {
    if (currentSession > 0) {
      setCurrentSession((prev) => prev - 1);
    }
  };

  const handleExitStudySession = () => {
    setShowStudySession(false);
    setCurrentSession(0);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-xl p-4">
        <p className="text-red-800 dark:text-red-400">Error: {error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-2 px-4 py-2 bg-red-100 hover:bg-red-200 dark:bg-red-800/50 dark:hover:bg-red-700/50 text-red-600 dark:text-red-400 rounded-lg font-medium transition-colors border border-red-300 dark:border-red-700"
        >
          {t("reviews.retry")}
        </button>
      </div>
    );
  }

  if (
    showStudySession &&
    totalPendingCount > 0 &&
    pendingReviews.length > 0 &&
    currentSession < pendingReviews.length
  ) {
    const currentReview = pendingReviews[currentSession];

    return (
      <StudySession
        review={currentReview}
        currentCard={currentSession + 1}
        totalCards={pendingReviews.length}
        onComplete={handleCompleteReview}
        onExit={handleExitStudySession}
        onNext={handleNext}
        onPrevious={handlePrevious}
        canGoNext={currentSession < pendingReviews.length - 1}
        canGoPrevious={currentSession > 0}
      />
    );
  }

  const groupedSessions = getGroupedSessions();

  return (
    <div className="space-y-6">
      <div id="start-review-button" className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div className="flex-1">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              {t("reviews.title")}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">
              {t("reviews.subtitle")}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            {totalPendingCount > 0 && (
              <button
                onClick={startStudySession}
                className="flex items-center gap-2 px-4 sm:px-6 py-3 bg-gradient-to-br from-indigo-500 to-purple-600 text-white rounded-xl font-semibold hover:from-indigo-600 hover:to-purple-700 transition-all transform hover:-translate-y-0.5 shadow-lg hover:shadow-xl justify-center"
              >
                <Play size={20} />
                <span className="sm:hidden">
                  {t("reviews.startReviewShort")} ({totalPendingCount})
                </span>
                <span className="hidden sm:inline">
                  {t("reviews.startReview")} ({totalPendingCount})
                </span>
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6" id="stats-cards">
          <div id="pending-reviews" className="bg-white dark:bg-gray-700 p-4 sm:p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-600">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center bg-red-100 dark:bg-red-900/30">
                <Clock
                  size={20}
                  className="sm:w-6 sm:h-6 text-red-600 dark:text-red-400"
                />
              </div>
              <div>
                <p className="text-gray-600 dark:text-gray-300 text-xs sm:text-sm mb-1">
                  {t("reviews.pendingToday")}
                </p>
                <p className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">
                  {totalPendingCount}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-700 p-4 sm:p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-600">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center bg-orange-100 dark:bg-orange-900/30">
                <BookOpen
                  size={20}
                  className="sm:w-6 sm:h-6 text-orange-600 dark:text-orange-400"
                />
              </div>
              <div>
                <p className="text-gray-600 dark:text-gray-300 text-xs sm:text-sm mb-1">
                  {t("reviews.next7Days")}
                </p>
                <p className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">
                  {upcoming7DaysCount}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-700 p-4 sm:p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-600">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center bg-purple-100 dark:bg-purple-900/30">
                <TrendingUp
                  size={20}
                  className="sm:w-6 sm:h-6 text-purple-600 dark:text-purple-400"
                />
              </div>
              <div>
                <p className="text-gray-600 dark:text-gray-300 text-xs sm:text-sm mb-1">
                  {t("reviews.next30Days")}
                </p>
                <p className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">
                  {totalPendingCount + totalUpcomingCount}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div id="progress-section">
        <ProgressSection />
      </div>

      <div id="calendar-widget" className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              {t("sessions.calendar")}
            </h2>
          </div>
        </div>
        <CalendarWidget />
      </div>

      <div id="review-session-list">
        <ReviewSessionList
          sessions={groupedSessions}
          onSessionsUpdate={handleSessionsUpdate}
          upcomingPagination={upcomingPagination}
          onUpcomingPageChange={handleUpcomingPageChange}
          pendingPagination={pendingPagination}
          onPendingPageChange={handlePendingPageChange}
        />
      </div>
    </div>
  );
};

export default SpacedRepetitionDashboard;
